import { Component, OnDestroy } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import * as fromReducer from 'app/core/store/reducers';
import * as fromSelectors from 'app/core/store/selectors';
import * as fromAssetAction from 'app/core/store/actions/delivery-assets.actions';
import { AsperaService } from 'app/core/services/aspera.service';
import { Delivery, DeliveryAsset, TrackTypes } from 'app/core/models';

export const DOWNLOAD_ALL_TYPE = 'All Formats';
export const DOWNLOAD_ALL = 'Download all';
export const STREAMING_TYPE = 'Streaming';

@Component({
  standalone: false,
  selector: 'pptl-modal-asset-download',
  templateUrl: './modal-asset-download.component.html',
  styleUrls: ['./modal-asset-download.component.scss'],
})
export class ModalAssetDownloadComponent implements OnDestroy {
  public assets: DeliveryAsset[] = [];
  public types: TrackTypes[] = [];
  public asperaTypes: TrackTypes[] = [];

  private isMedia = false;
  private isDownloadingAll = '';
  private delivery: Delivery;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private campaignTrackId?: string;

  constructor(
    public modalController: ModalController,
    private params: NavParams,
    private store: Store<fromReducer.State>,
    private asperaService: AsperaService,
  ) {
    this.assets = this.params.get('assets');
    this.isDownloadingAll = this.params.get('role');
    this.campaignTrackId = this.params.get('campaignTrackId');
    this.setIsMedia();
    this.setDelivery();
    this.combineTypes();
    this.combineAsperaTypes();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onAsperaButtonClick(trackType: TrackTypes = null): void {
    if (this.isDownloadAll) {
      this.asperaDownloadAll(trackType);
    } else if (trackType.type === DOWNLOAD_ALL_TYPE) {
      this.asperaDownloadAll(trackType, this.campaignTrackId);
    } else {
      this.asperaDownloadFile(trackType);
    }
    this.modalController.dismiss();
  }

  onClose() {
    this.modalController.dismiss();
  }

  downloadFileByType(trackType: TrackTypes = null): void {
    if (this.isDownloadingAll) {
      this.downloadAllFiles(trackType);
    } else {
      this.downloadFile(trackType);
    }
    this.modalController.dismiss('browser', this.isDownloadingAll);
  }

  downloadAllFiles(trackType: TrackTypes = null): void {
    const { type } = trackType;
    const { deliveryKeyCode, distributionName } = this.delivery;
    const isAllDownload = type === DOWNLOAD_ALL_TYPE;
    const assets = isAllDownload ? this.assets : this.getAssetsByType(trackType);
    const category = this.isFolderCategory(this.assets) ? 'folder' : assets[0].mediaType;

    this.store.dispatch(
      fromAssetAction.ToggleDownloadAll({
        payload: {
          category: category && category.toLowerCase(),
          name: distributionName,
          deliveryId: deliveryKeyCode,
          type: isAllDownload ? 'ALL' : type,
          campaignTrackId: '',
        },
      }),
    );

    this.modalController.dismiss('browser', this.isDownloadingAll);
  }

  get isDownloadAll(): boolean {
    return !this.campaignTrackId && typeof this.campaignTrackId !== 'number';
  }

  private combineTypes(): void {
    this.types = [...this.getMediaTypes(), ...this.getTypes()];
    this.types = this.getUniqueTypes();
    this.types = this.clearTypesFromStreaming();
    this.addDonwloadAllType();
  }

  private clearTypesFromStreaming(): TrackTypes[] {
    return this.types.filter((trackType: TrackTypes) => trackType.type !== STREAMING_TYPE);
  }

  private getUniqueTypes(): TrackTypes[] {
    return this.types.filter((value, i, self) => (
      self.findIndex((item: TrackTypes) => item.type === value.type) === i
    ));
  }

  private addDonwloadAllType(): void {
    if (this.types && this.types.length && this.types.length > 1) {
      this.types.unshift(<TrackTypes>{ type: DOWNLOAD_ALL_TYPE, url: '' });
    }

    if (this.types.length === 1 && this.isDownloadingAll) {
      const asperaDwlUrl = this.types[0].asperaDwlUrl ? this.types[0].asperaDwlUrl : '';
      this.types = [<TrackTypes>{ asperaDwlUrl, type: DOWNLOAD_ALL_TYPE, url: '' }];
    }
  }

  private combineAsperaTypes() {
    this.asperaTypes = this.types ? this.types.filter((type: TrackTypes) => type.asperaDwlUrl) : [];
    if (this.asperaTypes.length && this.asperaTypes.length > 1) {
      this.asperaTypes.unshift(<TrackTypes>{ type: DOWNLOAD_ALL_TYPE, url: '' });
    }
    if (this.asperaTypes && this.asperaTypes.length) {
      this.asperaService.init();
    }
  }

  private getAssetsByType(type: TrackTypes) {
    const assets = [];

    if (!this.isMedia) {
      return this.assets.filter((asset: DeliveryAsset) => asset.type === type.type);
    }
    this.assets.forEach((asset: any) => {
      if (asset.types) {
        asset.types.forEach((assetType: any) => {
          if (
            assetType.url.includes('stream') ||
            (type.type !== DOWNLOAD_ALL_TYPE && type.type !== assetType.type)
          ) {
            return;
          }
          const item = JSON.parse(JSON.stringify(asset));
          item.types = [assetType];
          assets.push(item);
        });
      }
    });
    return assets;
  }

  private asperaDownloadFile(type: TrackTypes): void {
    const asperaDwnUrl = type.asperaDwlUrl;
    const asset = this.assets.find((assetItem: DeliveryAsset) => (
      this.hasSameAsperaDwnUrl(assetItem, asperaDwnUrl)
    ));
    const campaignTrackId = asset && asset.campaignTrackId;

    this.asperaService.asperaDownload(asperaDwnUrl, campaignTrackId);
  }

  private asperaDownloadAll(trackType: TrackTypes, campaignTrackId = ''): void {
    const assets = this.assets.filter((asset: DeliveryAsset) => this.hasAsperaDwnUrl(asset));
    const { deliveryKeyCode } = this.delivery;
    const category = this.isFolderCategory(this.assets)
      ? 'folder'
      : assets[0].mediaType && assets[0].mediaType;
    const type = trackType.type === DOWNLOAD_ALL_TYPE ? 'all' : trackType.type;
    const assetsIds = assets.map((asset: DeliveryAsset) => asset.campaignTrackId);

    this.asperaService.asperaDownloadAll({
      assetsIds,
      deliveryKeyCode,
      type,
      campaignTrackId,
      category: category && category.toLowerCase(),
    });
  }

  private setIsMedia(): void {
    this.isMedia =
      this.assets.length &&
      (this.assets[0].category === 'audio' || this.assets[0].category === 'video');
  }

  private setDelivery(): void {
    this.store
      .pipe(select(fromSelectors.getDeliveryData), takeUntil(this.destroy$))
      .subscribe((resp: Delivery) => {
        this.delivery = resp;
      });
  }

  private downloadFile(trackType: TrackTypes = null): void {
    let downloads = [];
    if (trackType.type === DOWNLOAD_ALL_TYPE) {
      downloads = this.types.filter(
        (assetType: TrackTypes) => assetType.type !== DOWNLOAD_ALL_TYPE,
      );
    } else {
      downloads = [trackType];
    }

    if (downloads.length > 1) {
      const category = this.isFolderCategory(this.assets) ? 'folder' : this.assets[0].mediaType;
      this.store.dispatch(
        fromAssetAction.ToggleDownloadAllFormats({
          payload: {
            category,
            deliveryId: this.delivery.deliveryKeyCode,
            name: this.delivery.distributionName,
            type: 'ALL',
            campaignTrackId: this.assets[0].campaignTrackId,
          },
        }),
      );
    } else {
      this.startDownloading(downloads[0]);
    }
  }

  private startDownloading(donwload): void {
    const params = { ...donwload, campaignTrackId: this.campaignTrackId };

    this.store.dispatch(
      donwload.cdn
        ? fromAssetAction.ToggleCdnDownloading({ payload: params })
        : fromAssetAction.ToggleDownloading({ payload: params }),
    );
  }

  private getMediaTypes(): TrackTypes[] {
    const mediaAsset = this.assets.filter((asset: DeliveryAsset) => asset.types);
    const items = [].concat.apply(
      [],
      mediaAsset.map((asset: DeliveryAsset) =>
        [...asset.types].map((trackType: TrackTypes) => ({ ...trackType })),
      ),
    );

    items.map((item: any) => {
      item.name = this.assets[0] && this.assets[0].trackTitle;
      return item;
    });

    return items.filter((type: TrackTypes) => type.url);
  }

  private getTypes(): TrackTypes[] {
    const notMediaTypes = this.assets.filter(
      (asset: DeliveryAsset) => !asset.types || !asset.types.length,
    );
    return notMediaTypes.map((asset: any) => <TrackTypes>{
      type: asset.type,
      asperaDwlUrl: asset.asperaDwlUrl,
      url: asset.downloadUrl,
      cdn: asset.cdn,
      name: asset.name,
    });
  }

  private hasSameAsperaDwnUrl(asset: DeliveryAsset, respDwnUrl: string): boolean {
    return (
      asset.asperaDwlUrl === respDwnUrl ||
      !!(
        asset.types &&
        asset.types.find((item: TrackTypes) => item.asperaDwlUrl === respDwnUrl)
      )
    );
  }

  private hasAsperaDwnUrl(asset: DeliveryAsset): boolean {
    return (
      !!asset.asperaDwlUrl ||
      !!(
        asset.types &&
        asset.types.find((item: TrackTypes) => !!item.asperaDwlUrl)
      )
    );
  }

  private isFolderCategory(assets: DeliveryAsset[]): boolean {
    const currentType = assets[0].mediaType;

    return assets.some((asset: DeliveryAsset) => asset.mediaType !== currentType);
  }
}
